import { Component, DestroyRef, inject, model, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LayoutService } from '../../../../../../@vex/services/layout.service';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { BillingPeriod, CostAllocation, CostGroup, CostGroupService, CostGroupType } from '@eadsysteme/billingclient-v1';
import { SessionService } from '../../../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AllocationDialogComponent } from '../../basic-data/allocations/allocation-dialog/allocation-dialog.component';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { CostAllocationDetail } from '../../../../../services/data/CostAllocationDetail';
import { CostAllocationHelper } from '../../../../../services/CostAllocationHelper.service';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatTooltip } from '@angular/material/tooltip';
import { AllocationOptionComponent } from '../shared/allocation-option/allocation-option.component';

@Component({
	selector: 'vex-assign-allocation-dialog',
	imports: [
		MatDialogContent,
		MatDialogTitle,
		MatButton,
		MatDialogActions,
		MatIcon,
		TranslocoDirective,
		MatFormField,
		MatLabel,
		MatOption,
		MatSelect,
		MatSelectTrigger,
		FormsModule,
		CdkDrag,
		CdkDragHandle,
		MatTooltip,
		AllocationOptionComponent,
		MatIconButton,
	],
	templateUrl: './assign-allocation-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './assign-allocation-dialog.component.scss',
})
export class AssignAllocationDialogComponent implements OnInit {
	protected layoutService = inject(LayoutService);
	protected dialogRef = inject(MatDialogRef<AllocationDialogComponent>);
	protected data = inject(MAT_DIALOG_DATA);

	private readonly destroyRef = inject(DestroyRef);
	private readonly transloco = inject(TranslocoService);
	private readonly sessionService = inject(SessionService);

	private readonly costGroupService = inject(CostGroupService);
	private readonly snackBarService = inject(SnackbarService);
	private readonly costAllocationDetailService = inject(CostAllocationHelper);

	private readonly currentCostGroup: CostGroup = this.data.costGroup;
	private readonly billingId: string = this.data.billingId;
	private readonly billingPeriod: BillingPeriod = this.data.billingPeriod;

	protected costGroupType: CostGroupType = this.data.costGroupType;

	selectedAllocation: WritableSignal<CostAllocation> = model({});
	allocations = signal<CostAllocationDetail[]>([]);

	ngOnInit(): void {
		this.loadAllocations();
	}

	private loadAllocations() {
		this.costAllocationDetailService
			.loadAllocationDetails(this.billingId, this.billingPeriod.exportId, this.costGroupType.category)
			.then((details) => {
				this.allocations.set(details);

				if (this.currentCostGroup.costAllocationExportId) {
					const usedCostAllocation = this.allocations().filter((value) => value.exportId === this.currentCostGroup.costAllocationExportId);
					if (usedCostAllocation.length === 1) {
						this.selectedAllocation.set(usedCostAllocation[0]);
					}
				}
			})
			.catch((error) => console.error(error));
	}

	close(save: boolean) {
		if (!save) {
			this.dialogRef.close(save);
			return;
		}
		this.currentCostGroup.costAllocationExportId = this.selectedAllocation()?.exportId;
		this.costGroupService
			.updateCostGroup(this.sessionService.effectiveArea, this.billingId, this.currentCostGroup.exportId, this.currentCostGroup)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (savedCostGroup) => {
					this.dialogRef.close({ saved: true, savedCostGroup: savedCostGroup });
				},
				error: () => {
					this.snackBarService.error(this.transloco.translate('errors.savingBillingAllocation'));
				},
			});
	}

	protected getAllocationName() {
		return this.costAllocationDetailService.getAllocationDisplayName(this.selectedAllocation());
	}
}
